import { Box, Button, CircularProgress, Divider, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import EventNoteIcon from "@mui/icons-material/EventNote";
import {
  DocumentReferenceResponse,
  GetProjectResponse,
  useDeleteApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdDocumentsDocumentIdMutation,
  useGetApiConfigQuery,
} from "api/generatedApi";
import { api } from "api/enhancedEndpoints";
import { ScreenSizeEnum, useScreenSize } from "shared/use-screen-size";
import { useToast } from "shared/toast/hooks/use-toast";
import { useConfirm } from "components/shared/alert/confirm/hooks/use-confirm";
import { DesktopThreeStepNavigation } from "components/shared/three-step-navigation/desktop-three-step-navigation";
import { UploadDocument } from "shared/editors/hooks/upload-document";
import { ExtraWorkAgreementFormData, useValidateExtraWorkAgreementInput } from "./hooks/use-input-validation";
import { UseGetExtraWorkAgreementById } from "./hooks/use-get-extra-work-agreement-by-id";
import { useUpdateExtraWorkAgreement } from "./hooks/use-update-extra-work-agreement";
import { useHistoryDialog } from "./hooks/use-history-dialog";
import { useExtraWorkAgreementPermissions } from "./hooks/use-extra-work-agreement-permissions";
import { usePrintExtraWorkAgreement } from "./hooks/use-print-extra-work-agreement";
import { MobileOneStepBackBreadcrumb } from "./inputs/mobile-one-step-back";
import { DescriptionCard } from "./inputs/descriptionCard";
import { ExtraWACardWithHeader } from "./inputs/cardWithHeader";
import { DocumentCard } from "./inputs/documentCard";
import { DetailCard } from "./inputs/detailCard";
import { EditExtraWorkAgreementStatus } from "./inputs/agreement-status";
import { EditExtraWorkAgreementName } from "./inputs/name";

interface Props {
  project: GetProjectResponse;
}

export const ExtraWorkAgreementEditPage = (props: Props) => {
  const { project } = props;
  const { t } = useTranslation();
  const { screenSize } = useScreenSize();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();

  const { extraworkAgreementId } = useParams<"extraworkAgreementId">();
  const agreementId = extraworkAgreementId ?? "";

  const { agreement, isLoading } = UseGetExtraWorkAgreementById({
    projectId: project.id ?? "",
    agreementId,
  });

  const [editPageState, setEditPageState] = useState<"view" | "edit">("view");
  const [tempFilesforUpload, setTempFilesforUpload] = useState<File[]>([]);
  const [tempFilesForDelete, setTempFilesForDelete] = useState<DocumentReferenceResponse[]>([]);
  const [resetLocalDocumentSwitch, setResetLocalDocumentSwitch] = useState(false);

  const { hasValidateInputs } = useValidateExtraWorkAgreementInput();

  const { data: configData } = useGetApiConfigQuery();
  const documentsPath = `/api/projects/${project.id}/extraworkagreements/${agreementId}/documents`;
  const uploadDocument = UploadDocument(documentsPath, configData?.maxUploadFileSizeMB);
  const [deleteDocument] =
    useDeleteApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdDocumentsDocumentIdMutation();

  const updateExtraWorkAgreement = useUpdateExtraWorkAgreement({
    project,
    agreementId,
    onSuccess: () => setEditPageState("view"),
  });

  const { register, setValue, getValues, handleSubmit, control, watch, formState, reset } =
    useForm<ExtraWorkAgreementFormData>({
      mode: "all",
      defaultValues: {
        extraWorkAgreementType: "CustomerHours",
        workTime: { hours: 0, minutes: 0 },
        approvalStatus: agreement?.approvalStatus ?? "UnderDevelopment",
      },
    });

  const resetToAgreement = useCallback(() => {
    if (!agreement) return;
    reset({
      extraWorkAgreementType: agreement.extraWorkAgreementType,
      extraWorkAgreementNumber: agreement.extraWorkAgreementNumber,
      name: agreement.name,
      description: agreement.description,
      workTime: agreement.workTime,
      paymentDkr: agreement.paymentDkr,
      isPrivate: agreement.isPrivate,
      approvalStatus: agreement.approvalStatus,
    });
  }, [agreement, reset]);

  useEffect(() => {
    resetToAgreement();
  }, [resetToAgreement]);

  // If the agreement is no longer visible to the user (e.g. a Manager sent an
  // AwaitingApproval agreement back to UnderDevelopment and lost visibility),
  // bail out to the list rather than getting stuck on an empty form.
  useEffect(() => {
    if (!isLoading && !agreement && agreementId && project.id) {
      navigate(`/projects/${project.id}/dashboard/extraworkagreements`, { replace: true });
    }
  }, [isLoading, agreement, agreementId, project.id, navigate]);

  // Drop pending upload previews once the refetched agreement contains a saved doc with the same name.
  useEffect(() => {
    const savedDocs = agreement?.documents;
    if (!savedDocs || savedDocs.length === 0) return;
    const savedNames = new Set(savedDocs.map((d) => d.name).filter((n): n is string => !!n));
    setTempFilesforUpload((prev) => {
      const next = prev.filter((f) => !savedNames.has(f.name));
      return next.length === prev.length ? prev : next;
    });
  }, [agreement?.documents]);

  // Subscribe to all field changes so isInputValid / isDirty recompute each render.
  watch();

  const permissions = useExtraWorkAgreementPermissions({ project, agreement });

  const isInputValid = hasValidateInputs(getValues);
  const isDirty =
    formState.isDirty || tempFilesforUpload.length > 0 || tempFilesForDelete.length > 0;
  const canSaveEdit = isInputValid && isDirty;

  const isEditing = editPageState === "edit";
  const canEditFields = permissions.canEditFields(isEditing);
  const canEditStatus = permissions.canEditStatus(isEditing);
  const fieldsDisabled = !canEditFields;
  const statusDisabled = !canEditStatus;

  const openHistoryDialog = useHistoryDialog({
    auditLogEntries: agreement?.logEntries || [],
  });

  const printAgreement = usePrintExtraWorkAgreement({
    projectId: project.id ?? "",
    agreementId,
    agreementInternalNumber: agreement?.extraWorkAgreementInternalNumber,
  });

  const revertEdit = () => {
    resetToAgreement();
    setTempFilesforUpload([]);
    setTempFilesForDelete([]);
    setResetLocalDocumentSwitch((prev) => !prev);
    setEditPageState("view");
  };

  const openCancelConfirmEdit = useConfirm({
    title: t("dashboard.extraWorkAgreements.update.confirmCancelTitle"),
    description: t("dashboard.extraWorkAgreements.update.confirmCancelDescription"),
    submitButtonLabel: t("common.finish"),
    submit: revertEdit,
  });

  const onCancel = () => {
    if (isDirty) {
      openCancelConfirmEdit();
    } else {
      setEditPageState("view");
    }
  };

  const documentDelete = async (document: DocumentReferenceResponse) => {
    try {
      await deleteDocument({
        projectId: project.id ?? "",
        documentId: document.documentId ?? "",
        extraWorkAgreementId: agreementId,
      }).unwrap();
    } catch {
      toast.error(t("content.overview.documents.deleteError", { documentName: document.name }));
    }
  };

  const onSubmitEdit: SubmitHandler<ExtraWorkAgreementFormData> = async (data) => {
    if (!permissions.canEnterEditMode) {
      toast.error(t("dashboard.extraWorkAgreements.update.noPermission"));
      return;
    }

    await updateExtraWorkAgreement(data);

    for (const file of tempFilesforUpload) {
      await uploadDocument(file);
    }
    // Pending previews are kept until the refetched agreement contains matching saved documents;
    // that way freshly uploaded images remain visible while the server catches up.

    for (const document of tempFilesForDelete) {
      if (document.documentId) {
        await documentDelete(document);
      }
    }
    setTempFilesForDelete([]);

    dispatch(api.util.invalidateTags(["ExtraWorkAgreement"]));
  };

  if (isLoading) {
    return (
      <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={100} />
      </Box>
    );
  }

  const isViewing = editPageState === "view";

  return (
    <Box
      sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: screenSize === ScreenSizeEnum.Mobile ? 0 : 2,
          p: screenSize !== ScreenSizeEnum.LargeDesktop ? 2 : 8,
          backgroundColor: "grey",
          overflowY: "auto",
          height: "100%",
        }}
    >
      <Box>
        {screenSize === ScreenSizeEnum.Mobile ? (
          <MobileOneStepBackBreadcrumb
            from={t("dashboard.extraWorkAgreements.create.breadcrumbTitle2")}
            isDirty={isDirty}
            onClick={onCancel}
          />
        ) : (
          <DesktopThreeStepNavigation
            firstTitle={t("dashboard.extraWorkAgreements.create.breadcrumbTitle1")}
            secondTitle={t("dashboard.extraWorkAgreements.create.breadcrumbTitle2")}
            thirdTitle={agreement?.name || ""}
            onClick={onCancel}
            isDirty={isDirty}
            labelVariant="h5"
          />
        )}
      </Box>


      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
          {isViewing && (
            <Box sx={{ alignSelf: "center" }}>
              <IconButton
                onClick={printAgreement}
                sx={{ pt: screenSize === ScreenSizeEnum.Mobile ? 1 : 0 }}
              >
                <PrintIcon />
              </IconButton>
              <IconButton
                onClick={openHistoryDialog}
                sx={{ pt: screenSize === ScreenSizeEnum.Mobile ? 1 : 0 }}
              >
                <EventNoteIcon />
              </IconButton>
              {permissions.canEnterEditMode && (
                <IconButton
                  onClick={() => setEditPageState("edit")}
                  sx={{ pt: screenSize === ScreenSizeEnum.Mobile ? 1 : 0 }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: screenSize === ScreenSizeEnum.LargeDesktop ? "row" : "column-reverse",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", flex: 3, gap: 2 }}>
            {screenSize === ScreenSizeEnum.LargeDesktop && (
              <Box>
                <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.titleCardTitle")}>
                  <EditExtraWorkAgreementName
                    control={control}
                    agreement={agreement}
                    disabled={fieldsDisabled}
                  />
                </ExtraWACardWithHeader>
              </Box>
            )}

            <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.descriptionCardTitle")}>
              <DescriptionCard disabled={fieldsDisabled} control={control} />
            </ExtraWACardWithHeader>

            <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.documentationCardTitle")}>
              <DocumentCard
                agreement={agreement}
                disabled={fieldsDisabled}
                setTempFilesForDelete={setTempFilesForDelete}
                setTempFilesforUpload={setTempFilesforUpload}
                canEdit={canEditFields}
                documents={agreement?.documents ?? []}
                resetLocalDocumentSwitch={resetLocalDocumentSwitch}
                projectId={project.id ?? ""}
                pendingUploads={tempFilesforUpload}
              />
            </ExtraWACardWithHeader>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", flex: 2, gap: 2 }}>
            <Box
              sx={{ display: screenSize === ScreenSizeEnum.LargeDesktop ? "none" : "block" }}
            >
              <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.titleCardTitle")}>
                <EditExtraWorkAgreementName
                  control={control}
                  agreement={agreement}
                  disabled={fieldsDisabled}
                />
              </ExtraWACardWithHeader>
            </Box>

            <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.statusCardTitle")}>
              <EditExtraWorkAgreementStatus
                agreement={agreement}
                disabled={statusDisabled}
                register={register}
                watch={watch}
              />
            </ExtraWACardWithHeader>

            <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.detailsCardTitle")}>
              <DetailCard
                agreement={agreement}
                disabled={fieldsDisabled}
                register={register}
                getValue={getValues}
                setValue={setValue}
                control={control}
                watch={watch}
                editPageState={editPageState}
              />
            </ExtraWACardWithHeader>
          </Box>
        </Box>
      </Box>

      {editPageState === "edit" && (
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!canSaveEdit}
            onClick={handleSubmit(onSubmitEdit)}
            sx={{ width: "auto" }}
          >
            {t("dashboard.extraWorkAgreements.create.updateButton")}
          </Button>
          <Button variant="outlined" color="primary" onClick={onCancel} sx={{ width: "auto" }}>
            {t("dashboard.extraWorkAgreements.create.updateCancelButton")}
          </Button>
        </Box>
      )}

      {screenSize === ScreenSizeEnum.Mobile && <Box sx={{ height: "60px" }} />}
    </Box>
  );
};
